import React, { useMemo, useState } from "react";
import { getSidebarMenuConfig } from "./config/menu.config";
import { DASHBOARD_MENU_KEY } from "./config/menu.keys";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { PORTAL_ROLES } from "@constants/portalRoles";
import { selectPortalRole } from "@store/selectors/portalRoleSelectors";
import SidebarLogo from "./SidebarLogo";
import SidebarMenuItem from "./components/SidebarMenuItem";
import "./Sidebar.css";
import { openItemOnSideBarClick } from "./utils/openItemOnSideBarClick";

// Menu item type
interface MenuItem {
	key: string;
	label: string;
	icon: React.ReactNode;
	route: string;
	children?: MenuItem[];
}

const Sidebar: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();
	const portalRole = useSelector(selectPortalRole);
	const menu = useMemo(() => {
		const full = getSidebarMenuConfig(t);
		if (portalRole === PORTAL_ROLES.CHAIRMAN) {
			return full.filter(item => item.key === DASHBOARD_MENU_KEY);
		}
		return full;
	}, [t, portalRole]);
	const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

	const toggleMenu = (menuKey: string) => {
		setExpandedMenus(prev => (prev.includes(menuKey) ? prev.filter(key => key !== menuKey) : [...prev, menuKey]));
	};

	const isMenuExpanded = (menuKey: string) => expandedMenus.includes(menuKey);
	const isActiveRoute = (route: string) => {
		// Special handling for Course Builder routes
		if (route === "/course-builder") {
			return location.pathname.startsWith("/course-builder");
		}
		return location.pathname === route;
	};
	const isChildActive = (children: MenuItem[]) => {
		return children?.some(child => isActiveRoute(child.route));
	};

	// Recursive render for menu items
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const renderMenuItems = (items: MenuItem[], _parentKey: string = "", level: number = 0) =>
		items.map(item => {
			const hasChildren = Boolean(item.children && item.children.length > 0);
			const isExpanded = isMenuExpanded(item.key);
			const isActive = isActiveRoute(item.route);
			const hasActiveChild = hasChildren && isChildActive(item.children || []);
			return (
				<SidebarMenuItem
					key={item.key}
					item={item}
					level={level}
					isExpanded={isExpanded}
					isActive={isActive}
					hasActiveChild={hasActiveChild}
					onClick={() => openItemOnSideBarClick(item, hasChildren, toggleMenu, navigate)}
					toggleMenu={toggleMenu}
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					renderChildren={(children, _unusedParentKey) => renderMenuItems(children || [], item.key, level + 1)}
				/>
			);
		});

	return (
		<aside className="sidebar-bg w-72 h-screen flex flex-col items-center border-r fixed left-0 top-0 z-20">
			<SidebarLogo />
			<nav className="w-full flex-1 overflow-y-auto">
				<ul className="space-y-4 px-4 w-full">{renderMenuItems(menu)}</ul>
			</nav>
		</aside>
	);
};

export default Sidebar;
